import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tabs, Button, message, Modal } from "antd";
import { useHistory } from "react-router-dom";

import { withPermission } from "hocs/withPermission";
import { canEditPermission } from "configs/constants";

import AdminClientHeader from "../components/header/AdminClientHeader";
import CompanyInfoTab from "../components/form/CompanyInfoTab";
import BillingSettingsTab from "../components/form/BillingSettingsTab";
import BillingLogTab from "../components/form/BillingLogTab";
import PaymentMethodsTab from "../components/form/PaymentMethodsTab";
import withFetchOne from "../../../../hocs/withFetchOne";
import "./AdminCreateUpdateClient.scss";
import { doGetClient, doUpdateClient, blockClient, unblockClient } from "../store/thunks";
import { selectClient } from "../store/clientSlice";
import clientApi from "../store/api";

const AdminClientEdit = () => {
  const breadCrumbs = [{ text: "CLIENTS" }, { text: "EDIT CLIENT" }];
  const client = useSelector(selectClient);
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [pendingSaveData, setPendingSaveData] = useState(null);
  const isLoading = useSelector((state) => state.global.isLoading);
  const dispatch = useDispatch();
  const history = useHistory();

  // Refs to access form data from child components
  const companyInfoRef = useRef();
  const billingSettingsRef = useRef();

  const handleFormChange = () => {
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      let formData = {};
      let currentAddress = null;
      let isBlocked = false;
      let blockReason = null;

      // Get data from Company Info tab
      if (companyInfoRef.current) {
        const companyData = await companyInfoRef.current.getFormData();
        formData = { ...formData, ...companyData.values };
        currentAddress = companyData.currentAddress;
      }

      // Get billing settings data
      if (billingSettingsRef.current) {
        const billingData = billingSettingsRef.current.getFormData();
        formData.tierId = billingData.tierId;
        formData.facturaPaymentTerms = billingData.facturaPaymentTerms;
        isBlocked = billingData.isBlocked;
        blockReason = billingData.blockReason;
      }

      // Prepare payload similar to original implementation
      const { address, lngLat } = currentAddress || {
        address: client.address,
        lngLat: [client.lng, client.lat]
      };
      const payload = {
        ...formData,
        id: client.id,
        address,
        lng: Number(lngLat[0]),
        lat: Number(lngLat[1]),
        services: formData.services?.map((serviceId) => ({ id: serviceId })),
        driverIds: formData.driverIds
      };

      // Check if tier has changed
      const tierChanged = formData.tierId !== client.tierId && formData.tierId != null;

      if (tierChanged) {
        // Store the payload and show modal
        setPendingSaveData({ payload, isBlocked, blockReason });
        setShowSyncModal(true);
        return;
      }

      // If tier didn't change, proceed with normal save
      await performSave(payload, isBlocked, blockReason, false);
    } catch (error) {
      console.error("Save error:", error);
      message.error("An error occurred while saving");
    }
  };

  const performSave = async (payload, isBlocked, blockReason, syncEntitlements) => {
    try {
      // Store client ID before update (as updateClient API deletes it from payload)
      const clientId = client.id;

      // Update client
      const { error } = await dispatch(doUpdateClient(payload));

      if (!error) {
        // Sync entitlements if requested
        if (syncEntitlements) {
          try {
            const response = await clientApi.syncTierEntitlements(clientId);
            if (response && response.success) {
              message.success(`${response.message}`);
            } else {
              message.warning(
                "Client updated but entitlement sync returned unexpected response"
              );
            }
          } catch (syncError) {
            console.error("Entitlement sync error:", syncError);
            message.warning("Client updated but failed to sync entitlements");
          }
        }

        // Handle blocking/unblocking if status changed
        if (client && isBlocked !== client.isBlocked) {
          if (isBlocked) {
            const { error: blockError } = await dispatch(
              blockClient({
                clientId: client.id,
                blockReason: blockReason
              })
            );
            if (blockError) {
              message.error("Client updated but failed to block client");
              return;
            }
          } else {
            const { error: unblockError } = await dispatch(
              unblockClient({
                clientId: client.id,
                blockReason: blockReason
              })
            );
            if (unblockError) {
              message.error("Client updated but failed to unblock client");
              return;
            }
          }
        }

        if (!syncEntitlements) {
          message.success("Client updated successfully");
        }
        setHasChanges(false);
        history.push("/admin/clients");
      } else {
        message.error("Failed to update client");
      }
    } catch (error) {
      console.error("Save error:", error);
      message.error("An error occurred while saving");
    }
  };

  const handleSyncModalYes = async () => {
    setShowSyncModal(false);
    if (pendingSaveData) {
      await performSave(
        pendingSaveData.payload,
        pendingSaveData.isBlocked,
        pendingSaveData.blockReason,
        true
      );
      setPendingSaveData(null);
    }
  };

  const handleSyncModalNo = async () => {
    setShowSyncModal(false);
    if (pendingSaveData) {
      await performSave(
        pendingSaveData.payload,
        pendingSaveData.isBlocked,
        pendingSaveData.blockReason,
        false
      );
      setPendingSaveData(null);
    }
  };

  const tabItems = [
    {
      key: "1",
      label: "Company Info",
      children: (
        <CompanyInfoTab
          ref={companyInfoRef}
          client={client}
          onChange={handleFormChange}
        />
      )
    },
    {
      key: "2",
      label: "Billing Settings",
      children: (
        <BillingSettingsTab
          ref={billingSettingsRef}
          client={client}
          onChange={handleFormChange}
        />
      )
    },
    {
      key: "3",
      label: "Payment Methods",
      children: <PaymentMethodsTab client={client} />
    },
    {
      key: "4",
      label: "Factura Log",
      children: <BillingLogTab client={client} />
    }
  ];

  return (
    <div className="admin-client-edit admin-create-update-client">
      <AdminClientHeader breadCrumbs={breadCrumbs} hasButton={false} />
      <div className="admin-content form-content">
        <div className="edit-client-header">
          <h2 className="title">Edit Client</h2>
          <div className="header-controls">
            <Button
              type="primary"
              disabled={!hasChanges}
              loading={isLoading}
              onClick={handleSave}
              className={hasChanges ? "save-button-active" : "save-button-inactive"}
            >
              Save
            </Button>
          </div>
        </div>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          className="client-edit-tabs"
        />
      </div>

      {/* Tier Entitlements Sync Modal */}
      <Modal
        title="Update Entitlements"
        open={showSyncModal}
        onCancel={() => setShowSyncModal(false)}
        footer={[
          <Button key="no" onClick={handleSyncModalNo}>
            No
          </Button>,
          <Button key="yes" type="primary" onClick={handleSyncModalYes}>
            Yes
          </Button>
        ]}
      >
        <p>Do you want to update all entitlements to match the new tier?</p>
      </Modal>
    </div>
  );
};
export default withPermission(
  withFetchOne(AdminClientEdit, {
    fetchData: doGetClient
  }),
  canEditPermission
);
